import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { GalleryHorizontalEndIcon, ImageIcon, Loader2 } from "lucide-react";
import type { MarketingResource } from "creso-core/types";
import { getRouteApi } from "@tanstack/react-router";
import {
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  V2Loader,
  SearchInput,
} from "web-design-system";
import { Page } from "@core/components/layout/page";
import { FilterList } from "@core/components/filter-list";
import { getResourcePreviewImage, isResourceNew } from "creso-core/utils";
import {
  getSelectedTopicIds,
  useResourceTopicFilters,
} from "../hooks/use-resource-topic-filters";
import { useInfiniteMarketingResources } from "../hooks/use-infinite-marketing-resources";
import { MarketingPosterPreviewDialog } from "../components/marketing-poster-preview-dialog";
import { WebStoryPreviewDialog } from "../components/web-story-preview-dialog";

const PAGE_SIZE = 10;
type MarketingTab = "marketing_poster" | "web_story";
const routeApi = getRouteApi("/(auth)/_headed/resources/_layout/marketing");

function MarketingCard({
  item,
  onOpen,
}: {
  item: MarketingResource;
  onOpen: () => void;
}) {
  const previewImage = getResourcePreviewImage(item);
  const isWebStory = item.resource_type === "web_story";

  if (!previewImage) {
    return null;
  }

  return (
    <div
      className="group relative w-full cursor-pointer overflow-hidden rounded-xl border border-border/50 bg-card text-left flex flex-col"
      onClick={onOpen}
    >
      {isResourceNew(item) ? (
        <Badge
          variant="custom"
          className="absolute left-3 top-3 rounded-sm bg-tag-orange-foreground px-1.5 py-0.5 text-xxs text-tag-orange"
        >
          New
        </Badge>
      ) : null}
      <img
        src={previewImage}
        alt={item.title}
        loading="lazy"
        className="aspect-[4/5] w-full object-center h-full"
      />
      <div className="flex justify-between gap-3 p-4 h-[56px] text-foreground bg-background items-center shrink-0">
        <div className="line-clamp-2 text-sm font-medium">{item.title}</div>
        <span aria-hidden className="shrink-0">
          {isWebStory ? (
            <GalleryHorizontalEndIcon className="size-4.5 text-tag-yellow-foreground" />
          ) : (
            <ImageIcon className="size-4.5 text-tag-yellow-foreground" />
          )}
        </span>
      </div>
    </div>
  );
}

export default function MarketingResourcesPage() {
  const navigate = routeApi.useNavigate();
  const { category } = routeApi.useSearch();
  const selectedTabFromUrl: MarketingTab =
    category === "web_story" ? "web_story" : "marketing_poster";
  const [selectedType, setSelectedType] =
    useState<MarketingTab>(selectedTabFromUrl);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(
    null
  );
  const resourceType =
    selectedType === "marketing_poster" ? "marketing_poster" : "web_story";
  const { filterOptions } = useResourceTopicFilters(resourceType);
  const selectedTopicIds = getSelectedTopicIds(selectedFilters);
  const topicIdsKey = selectedTopicIds.join(",");
  const topicIds = selectedTopicIds.length > 0 ? selectedTopicIds : undefined;

  const {
    page,
    resourcesAccumulated,
    isFetching,
    isInitialLoading,
    scrollContainerRef,
    loadMoreSentinelRef,
  } = useInfiniteMarketingResources({
    resource_type: resourceType,
    search: debouncedSearch || undefined,
    topic_ids: topicIds,
    page_size: PAGE_SIZE,
    listResetKey: `${selectedType}|${debouncedSearch}|${topicIdsKey}`,
  });

  const isInitialTabMountRef = useRef(true);

  useEffect(() => {
    setSelectedType(selectedTabFromUrl);

    if (isInitialTabMountRef.current) {
      isInitialTabMountRef.current = false;
      return;
    }

    setSelectedFilters([]);
  }, [selectedTabFromUrl]);

  const effectiveResources = useMemo(() => {
    if (resourcesAccumulated.length > 0) {
      return resourcesAccumulated;
    }

    if (isInitialLoading) {
      return [];
    }

    return [];
  }, [resourcesAccumulated, isInitialLoading]);

  const toggleOption = (option: string) => {
    setSelectedFilters((current) =>
      current.includes(option)
        ? current.filter((value) => value !== option)
        : [...current, option]
    );
  };

  const clearFilters = () => {
    setSelectedFilters([]);
    setSearch("");
  };

  useEffect(() => {
    if (
      selectedResourceId &&
      !effectiveResources.some((resource) => resource.id === selectedResourceId)
    ) {
      setSelectedResourceId(null);
    }
  }, [effectiveResources, selectedResourceId]);

  const filters = useMemo(() => {
    return [
      {
        title: "Topics",
        options: filterOptions.map((option) => ({
          id: option.key,
          label: option.label,
          count: option.count,
          checked: selectedFilters.includes(option.key),
          onCheckedChange: () => toggleOption(option.key),
        })),
      },
    ];
  }, [filterOptions, selectedFilters]);

  return (
    <>
      <Page.Header title="Marketing" />

      <Page.Body className="overflow-hidden">
        <Tabs
          value={selectedType}
          onValueChange={(value) => {
            const nextType = value as MarketingTab;
            if (nextType === selectedType) return;

            setSelectedType(nextType);
            setSelectedFilters([]);
            navigate({
              search: (prev: Record<string, unknown>) => ({
                ...prev,
                category: nextType,
              }),
              replace: true,
            });
          }}
          className="flex min-h-0 sticky flex-1 flex-col gap-4 overflow-hidden h-full"
        >
          <div className="flex shrink-0 flex-row gap-3 items-center justify-between">
            <TabsList className="h-auto w-fit justify-start rounded-lg bg-muted p-1">
              <TabsTrigger value="marketing_poster">Creatives</TabsTrigger>
              <TabsTrigger value="web_story">Web Stories</TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <div className="w-80 pr-1">
                <SearchInput
                  value={search}
                  placeholder="Search for topics / keywords"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="lg:hidden">
                <FilterList
                  showAccordion={false}
                  filters={filters}
                  onClearAll={clearFilters}
                />
              </div>
            </div>
          </div>

          <div className="gap-4 flex flex-row flex-1 overflow-hidden">
            <div className="hidden self-stretch lg:block">
              <FilterList
                showAccordion={false}
                filters={filters}
                onClearAll={clearFilters}
              />
            </div>

            <V2Loader
              isLoading={isInitialLoading}
              className="flex flex-1 overflow-hidden"
            >
              <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto pr-1"
              >
                {effectiveResources.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-5">
                    {effectiveResources.map((item) => (
                      <MarketingCard
                        key={item.id}
                        item={item}
                        onOpen={() => setSelectedResourceId(item.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card px-6 text-center">
                    <div className="text-sm font-medium">
                      No marketing assets match the current search or filters.
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      Try a different keyword or clear the applied filters.
                    </div>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </Button>
                  </div>
                )}
                <div
                  ref={loadMoreSentinelRef}
                  className="flex items-center justify-center"
                >
                  {page > 1 && isFetching ? (
                    <Loader2
                      className="size-5 animate-spin text-muted-foreground py-6"
                      aria-hidden
                    />
                  ) : null}
                </div>
              </div>
            </V2Loader>
          </div>
        </Tabs>
        {selectedType === "web_story" ? (
          <WebStoryPreviewDialog
            items={effectiveResources}
            selectedId={selectedResourceId}
            onSelectedIdChange={setSelectedResourceId}
          />
        ) : (
          <MarketingPosterPreviewDialog
            items={effectiveResources}
            selectedId={selectedResourceId}
            onSelectedIdChange={setSelectedResourceId}
          />
        )}
      </Page.Body>
    </>
  );
}
